import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useCallback, useMemo } from 'react'
import { StatusBar } from 'react-native'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { translate } from '../../i18n'
import { LeaderBoardView } from './LeaderBoardView'
import { LeaderboardPlayer } from '../../components/ANewDesign/LeaderboardList/LeaderboardList'
import { useGameQuery } from '../../hooks/convex/useGameState'
import { useGameContext } from '../GameContainer/GameContext'
import { useStore } from '@/models'

const DEFAULT_RUN_TIME = '00:00:00'

function formatRunTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function getRunTimeFromGame(
  game: { startedAt?: number | null; finishedAt?: number | null; state: string } | undefined
): string {
  if (!game || game.startedAt == null) {
    return DEFAULT_RUN_TIME
  }
  const startMs = game.startedAt
  const endMs = game.state === 'finished' && game.finishedAt != null ? game.finishedAt : Date.now()
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000))
  return formatRunTime(seconds)
}

export const LeaderBoardScreenConvex = () => {
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const store = useStore()
  const params = useLocalSearchParams<{ gameId?: string; userId?: string }>()
  const context = useGameContext()

  // Resolve gameId/userId: use context when inside GameContainer, else route params (leaderboard is at root so context is empty when opened from game drawer)
  const gameId = (context.gameId ?? (params.gameId as Id<'games'> | undefined)) as Id<'games'> | undefined
  const userId =
    (context.gameId && context.userId ? context.userId : null) ??
    params.userId ??
    store.session.user?.auth0Id ??
    ''

  const { game, players, myPlayer } = useGameQuery(gameId, userId)

  useFocusEffect(
    useCallback(() => {
      StatusBar.setBarStyle('light-content')
    }, [])
  )

  const loading =
    gameId == null || userId === '' || players === undefined || game === undefined

  const leaderboardPlayers: LeaderboardPlayer[] = useMemo(
    () =>
      (players ?? [])
        .map((p) => ({
          playerId: p.userId,
          playerName: p.name,
          chips: p.chips,
          isLeft: p.state === 'out',
          isRemovedByTeacher: undefined
        }))
        .sort((a, b) => (b.chips ?? 0) - (a.chips ?? 0)),
    [players]
  )

  const myChips = myPlayer?.chips ?? 0
  const myRank = useMemo(() => {
    const list = players ?? []
    if (myChips === 0) {
      return list.filter((p) => p.chips > 0).length + 1
    }
    return list.filter((p) => p.chips > myChips).length + 1
  }, [players, myChips])

  const prizePool = useMemo(
    () => leaderboardPlayers.reduce((sum, p) => (p.chips ? sum + p.chips : sum), 0),
    [leaderboardPlayers]
  )

  const runTime = useMemo(() => getRunTimeFromGame(game), [game])

  return (
    <LeaderBoardView
      loading={loading}
      title={translate('leaderboard.leaderboardHeading')}
      onClose={() => router.back()}
      paddingTop={insets.top}
      players={leaderboardPlayers}
      myPlayerId={myPlayer?.userId}
      myRank={myRank}
      runTime={runTime}
      prizePool={prizePool}
    />
  )
}
